import React, { useState, useRef, useEffect } from "react";
import BotResponseText from "./BotResponseText";
import { CheckCircle, Eye, Trash2, Upload } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import AudioResponsePlayer from "./AudioResponsePlayer";
import {
  selectIsVoiceFlow,
  selectVoiceFlowLocks,
  selectSelectedChatId,
  selectGrievanceFileData,
  selectGrievanceAdditionalComments,
  selectAccumulatedFinalTranscript,
  selectCurrentInterimTranscript,
} from "../../../../store/features/message/textContentSelectors";
import useLocalization from "../../../../hooks/useLocalization";
import { useAppContext } from "../../../../context/AppContext";
import PropTypes from "prop-types";
import DocumentViewerModal from "./DocumentViewerModal";
import {
  setGrievanceAdditionalComments,
  setGrievanceFileData,
} from "../../../../store/features/message/textContentSlice";
import { KpmgTransliterate } from "../../TranslitrationSuggestion/KpmgTransliterate";
import { TextareaAutosize } from "@mui/material";
import { ORGANISATION_CATEGORY_CODES } from "../../../../utils/constants";
import SocketMicStreamer from "../../InputUI/SocketMicStreamerNewUi";

const GrievanceInfoCard = ({
  grievance_bucket,
  updated_grievance,
  onVerifyClick,
  field_form,
  isMostRecent,
  contentAudio,
  mandatory_data_present,
  field_form_list,
  category_code = null,
  isNotSubcategory = false,
}) => {
  const isOrganisationCategory =
    category_code && ORGANISATION_CATEGORY_CODES.includes(category_code);

  const dispatch = useDispatch();
  const [showText, setShowText] = useState(true);
  const fileData = useSelector(selectGrievanceFileData);
  const accumulatedFinalTranscript = useSelector(
    selectAccumulatedFinalTranscript
  );

  const currentInterimTranscript = useSelector(selectCurrentInterimTranscript);

  const [isEditOpen, setIsEditOpen] = useState(true);
  //   const [notes, setNotes] = useState("");
  const notes = useSelector(selectGrievanceAdditionalComments);

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const autosaveTimerRef = useRef(null);
  const savedIndicatorTimerRef = useRef(null);
  const micRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  /*
fileData structure:
{
  name: string,
  type: string,
  size: number,
  buffer: ArrayBuffer,
  base64: string
}
*/
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const { t } = useLocalization();
  const { selectedLanguageData } = useAppContext();

  const selectedChatId = useSelector(selectSelectedChatId);
  const isVoiceFlow = useSelector(selectIsVoiceFlow);
  const isVoiceFlowLocked = useSelector(selectVoiceFlowLocks(selectedChatId));
  const fileInputRef = useRef(null);
  const currentNotesRef = useRef("");

  useEffect(() => {
    currentNotesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    const voiceText = [
      accumulatedFinalTranscript?.trim(),
      currentInterimTranscript?.trim(),
    ]
      .filter(Boolean)
      .join(" ");

    if (!voiceText) return;

    // 🚨 prevent overwrite loop
    if (currentNotesRef.current === voiceText) return;

    dispatch(setGrievanceAdditionalComments(voiceText));
  }, [accumulatedFinalTranscript, currentInterimTranscript]);

  // 🔹 autosave (debounced)
  const handleNotesChange = (text) => {
    // setNotes(text);
    dispatch(setGrievanceAdditionalComments(text));
    setIsSaved(false);
    setIsSaving(true);

    // Clear previous timers
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    if (savedIndicatorTimerRef.current) {
      clearTimeout(savedIndicatorTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      // 👉 Put your API / Redux save here
      // dispatch(saveNotes(text))
      // console.log("Autosaved notes:", text);

      setIsSaving(false);
      setIsSaved(true);

      // Hide "Saved" after 2 seconds
      savedIndicatorTimerRef.current = setTimeout(() => {
        setIsSaved(false);
      }, 2000);
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (savedIndicatorTimerRef.current)
        clearTimeout(savedIndicatorTimerRef.current);
    };
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      // "image/png",
      // "image/jpeg",
      "application/pdf",
      // "application/msword",
      // "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type");
      return;
    }

    const arrayBuffer = await file.arrayBuffer();

    // Convert to Base64 (useful for API payloads)
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      )
    );

    dispatch(
      setGrievanceFileData({
        name: file.name,
        type: file.type,
        size: file.size,
        // buffer: arrayBuffer,
        base64,
      })
    );
  };

  const handleViewFile = () => {
    // if (!fileData) return;

    // const blob = new Blob([fileData.buffer], { type: fileData.type });
    // const url = URL.createObjectURL(blob);
    // window.open(url, "_blank");
    if (!fileData) return;
    setIsViewerOpen(true);
  };

  const handleDeleteFile = () => {
    dispatch(setGrievanceFileData(null));
    // IMPORTANT: reset file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isFieldFormContainsData = () =>
    Array.isArray(field_form) && field_form.length > 0;
  return (
    <div className=" space-y-3">
      <h3 className="text-lg font-bold mb-2 mt-2 flex gap-2 items-center">
        {" "}
        <CheckCircle className="w-5 h-5 text-green-600" />{" "}
        {t("grievanceInformation")}
      </h3>

      <div className="dark:bg-[#232323] border border-[#FFD6A8] rounded-lg shadow-sm p-3 bg-[#FFFBEB] border-[#9EB3FF]">
        <h4 className="text-md font-semibold text-[#000000] dark:text-blue-400">
          {t("grievanceSummery")}
        </h4>
        <div className="space-y-2 text-sm max-h-60 overflow-auto pr-2">
          <BotResponseText
            text={updated_grievance || "No summary available."}
          />
        </div>
      </div>

      {!isVoiceFlow &&
        !isVoiceFlowLocked &&
        isFieldFormContainsData() &&
        mandatory_data_present && (
          <AudioResponsePlayer
            isAutoPlay={isVoiceFlow && isMostRecent}
            contentAudio={contentAudio}
            showText={showText}
            setShowText={setShowText}
          />
        )}

      <div className="bg-white dark:bg-[#232323] border border-[#FFD6A8] rounded-lg shadow-sm">
        <h4 className="text-md font-semibold text-[#000000] px-3 py-1 border border-b-[#D9D9D9]">
          {t("categorization.title")}
        </h4>
        <div className="space-y-2 text-sm p-3">
          {Object.entries(grievance_bucket)
            // .filter(([k, v]) => k !== 'Description' && v)
            .filter(([k, v]) => {
              if (k === "Description" || !v) return false;
              if (isNotSubcategory && k === "SubCategory") return false;
              return true;
            })
            .map(([k, v], i) => (
              <div key={k}>
                <p className="w-40 font-medium text-gray-800 dark:text-gray-200">
                  {/* {t(`categorization.${k}`) || k} */}
                  {k === "Category" && isOrganisationCategory
                    ? t("categorization.Organisation") || "Organisation"
                    : t(`categorization.${k}`) || k}
                </p>
                <p className="text-gray-700 dark:text-gray-300 bg-[#F9FAFB] border border-[#FFD6A8] px-3 py-[6px] rounded-md mt-1">
                  {String(v)}
                </p>
              </div>
            ))}
        </div>
      </div>

      {Array.isArray(field_form_list) && field_form_list.length > 0 && (
        <div className="bg-white dark:bg-[#232323] border border-[#FFD6A8] rounded-lg shadow-sm">
          <div className="flex justify-between items-center px-3 py-2 border-b border-[#FFD6A8]">
            <h4 className="text-lg font-semibold text-[#000000] dark:text-green-400 flex items-center gap-2">
              {t("collectedUserInformation") || "Collected User Information"}
            </h4>
          </div>

          <div className="space-y-2 text-sm max-h-60 overflow-auto p-3">
            {field_form_list.map((item, idx) => {
              const value =
                item.FieldType === "Dropdown"
                  ? item.UserInputText
                  : item.UserInputValue;

              return (
                <div
                  key={item.FieldName}
                  className="flex flex-col sm:flex-row sm:items-center border-b border-[#FFD6A8] dark:border-gray-700 pb-1 mb-1 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md px-1"
                >
                  <span className="w-full sm:w-48 font-semibold text-gray-800 dark:text-gray-100">
                    {selectedLanguageData?.code === "en"
                      ? item.DisplayLable
                      : item.DisplayLable_regional || item.DisplayLable}
                    :
                  </span>
                  <span className="text-gray-700 dark:text-gray-300 sm:ml-2">
                    {value || "-"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* File Upload */}
      <div className="flex items-center gap-4 p-3 bg-white dark:bg-[#232323] border border-[#FFD6A8] rounded-lg shadow-sm">
        <label className="flex items-center gap-2 cursor-pointer text-[#4A5565]">
          <Upload className="w-5 h-5" />
          <span className="text-sm font-medium">
            {t("supportDocument") || "Upload document"}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>

        {/* View */}
        <button
          type="button"
          onClick={handleViewFile}
          disabled={!fileData}
          className="flex items-center"
          style={{
            color: fileData ? "#FC6C02" : "#9CA3AF",
            cursor: fileData ? "pointer" : "not-allowed",
          }}
        >
          <Eye className="w-5 h-5" />
        </button>

        {/* Delete */}
        <button
          type="button"
          onClick={handleDeleteFile}
          disabled={!fileData}
          className="flex items-center"
          style={{
            color: fileData ? "#FC6C02" : "#9CA3AF",
            cursor: fileData ? "pointer" : "not-allowed",
          }}
        >
          <Trash2 className="w-5 h-5" />
        </button>

        {fileData && (
          <span className="text-xs truncate max-w-[200px] text-[#FC6C02]">
            {fileData.name}
          </span>
        )}
      </div>

      {/* Notes */}
      <div className="bg-white border border-[#FFD6A8] rounded-lg shadow-sm">
        <button
          type="button"
          onClick={() => setIsEditOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-800 dark:text-blue-400"
        >
          <span>{t("additionalNotes") || "Additional Notes"}</span>
          <span className="text-xs text-gray-500">
            {isEditOpen ? t("collapse") : t("expand")}
          </span>
        </button>

        {/* Editor */}
        {isEditOpen && (
          <div className="p-3 border-t border-[#FFD6A8]">
            <div className="relative">
              {/* TEXTAREA */}
              <KpmgTransliterate
                lang={selectedLanguageData?.code || "en"}
                value={notes}
                onChangeText={handleNotesChange}
                containerClassName="w-full"
                enabled={selectedLanguageData?.code !== "en"}
                renderComponent={(props) => (
                  <TextareaAutosize
                    {...props}
                    minRows={3}
                    maxRows={6}
                    placeholder={t("typeHere") || "Type here…"}
                    className="
                    w-full resize-none text-sm px-3 py-2 pr-10 border rounded-md border-[#FFD6A8]
                    focus:outline-none focus:ring-[#FFD6A8] focus:border-[#FFD6A8]
                    dark:bg-[#1f1f1f] dark:text-white
                  "
                  />
                )}
              />

              {/* 🎤 MIC BUTTON (BOTTOM RIGHT) */}
              {/* 🎤 MIC BUTTON (BOTTOM RIGHT) */}
<div className="absolute bottom-2 right-2 group">

  {/* Tooltip */}
  <div className="
    absolute bottom-full right-0 mb-2
    px-2 py-1 text-[11px] text-white rounded-md whitespace-nowrap
    opacity-0 group-hover:opacity-100
    pointer-events-none transition-opacity duration-200
    bg-[#FF6900]
  ">
    {isRecording ? t("tapToStop") : t("tapToSpeak")}
    {/* Arrow — must match bg color */}
    <div className="absolute top-full right-3 w-0 h-0
      border-l-4 border-r-4 border-t-4
      border-l-transparent border-r-transparent
      border-t-[#FF6900]"
    />
  </div>

  {/* Mic button */}
  <div
    onClick={() => setIsRecording(r => !r)}
    className={`
      relative rounded-full p-1.5 transition-all duration-300 cursor-pointer
      flex items-center justify-center
      ${isRecording
        ? "bg-red-500 dark:bg-red-600"
        : "bg-[#FF6900] dark:bg-[#FF6900]"
      }
    `}
  >
    {isRecording && (
      <>
        <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60 animate-ping" />
        <span className="absolute inline-flex h-[140%] w-[140%] rounded-full bg-red-300 opacity-30 animate-ping [animation-delay:300ms]" />
      </>
    )}

    <div className="relative z-10 [&_svg]:text-white [&_svg]:stroke-white [&_*]:text-white [&_*]:stroke-white">
      <SocketMicStreamer
        ref={micRef}
        normalButton={true}
        isUserInputBar={true}
        iconColor="#ffffff"
      />
    </div>
  </div>
</div>
            </div>

            {/* Autosave UX */}
            <div className="flex items-center gap-2 mt-2 h-4">
              {isSaving && (
                <div className="flex items-center gap-1 text-[11px] text-blue-600 animate-pulse">
                  <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  <span>{t("saving") || "Saving…"}</span>
                </div>
              )}

              {isSaved && !isSaving && (
                <div className="flex items-center gap-1 text-[11px] text-green-600">
                  <span>✔</span>
                  <span>{t("saved") || "Saved"}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Verify */}
      <div className="text-right">
        <button
          onClick={onVerifyClick}
          className="px-6 py-2.5 text-white text-sm rounded-md shadow-md"
          style={{ backgroundColor: "#FC6C02" }}
        >
          {t("verifyDetails")}
        </button>
      </div>

      {isViewerOpen && (
        <DocumentViewerModal
          fileData={fileData}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </div>
  );
};

GrievanceInfoCard.propTypes = {
  grievance_bucket: PropTypes.object,
  updated_grievance: PropTypes.string,
  mandatory_questions_answered: PropTypes.array,
  onVerifyClick: PropTypes.func.isRequired,
  field_form: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isMostRecent: PropTypes.bool,
  contentAudio: PropTypes.string,
  mandatory_data_present: PropTypes.bool,
  field_form_list: PropTypes.array,
};

export default GrievanceInfoCard;
